import * as THREE from 'three';

export function isPointNearPlane(
  point: THREE.Vector3,
  planeCS: THREE.Matrix4,
  maxDistanceToPoint: number,
  epsilon = 0.01
): boolean {
  const plane = new THREE.Plane(new THREE.Vector3(0, 0, -1), 0);
  plane.applyMatrix4(planeCS);
  const isNearPlane = Math.abs(plane.distanceToPoint(point)) <= maxDistanceToPoint + epsilon;
  return isNearPlane;
}

export function getDistanceFromPointToPlane(point: THREE.Vector3, planeCS: THREE.Matrix4): number {
  const plane = new THREE.Plane(new THREE.Vector3(0, 0, -1), 0);
  plane.applyMatrix4(planeCS);
  return plane.distanceToPoint(point);
}

function _expandBoundingBoxByBufferAttributes(
  bbox: THREE.Box3,
  mtx: THREE.Matrix4,
  attr: THREE.InterleavedBufferAttribute | THREE.BufferAttribute
): void {
  const pt = new THREE.Vector3();
  for (let i = 0, cnt = attr.count; i < cnt; ++i) {
    pt.fromBufferAttribute(attr, i);
    pt.applyMatrix4(mtx);
    bbox.expandByPoint(pt);
  }
}

export function computeBoundingBox(geometry: THREE.BufferGeometry, matrix: THREE.Matrix4): THREE.Box3 {
  const boundingBox = new THREE.Box3();
  boundingBox.makeEmpty();

  if (geometry.attributes.instanceStart !== undefined && geometry.attributes.instanceEnd !== undefined) {
    _expandBoundingBoxByBufferAttributes(boundingBox, matrix, geometry.attributes.instanceStart);
    _expandBoundingBoxByBufferAttributes(boundingBox, matrix, geometry.attributes.instanceEnd);
  } else if (geometry.attributes.position !== undefined) {
    _expandBoundingBoxByBufferAttributes(boundingBox, matrix, geometry.attributes.position);
  }
  return boundingBox;
}

export function getFittedCameraToBBoxParams(
  cameraMatrixWorld: THREE.Matrix4,
  bBox: THREE.Box3
): {
  cameraTarget: THREE.Vector3;
  viewWidth: number;
  viewHeight: number;
} {
  const cameraTarget = bBox.getCenter(new THREE.Vector3()).applyMatrix4(cameraMatrixWorld);
  const size = bBox.getSize(new THREE.Vector3());
  return {
    cameraTarget: cameraTarget,
    viewWidth: size.x,
    viewHeight: size.y
  };
}

export function getFittedCameraToObjectsParams(
  cameraMatrixWorld: THREE.Matrix4,
  fitObjects: Array<THREE.Mesh>
): {
  cameraTarget: THREE.Vector3;
  viewWidth: number;
  viewHeight: number;
} {
  const bBox = new THREE.Box3();

  for (const fitMesh of fitObjects) {
    fitMesh.updateMatrixWorld();
    if (!fitMesh.visible) {
      continue;
    }
    bBox.union(computeBoundingBox(fitMesh.geometry, cameraMatrixWorld.clone().invert().multiply(fitMesh.matrixWorld)));
  }

  return getFittedCameraToBBoxParams(cameraMatrixWorld, bBox);
}

export function makeRotationMatrix(
  position: THREE.Vector3,
  direction: THREE.Vector3,
  angle_radians: number
): THREE.Matrix4 {
  const dir = direction.clone().normalize();

  const cos_alpha = Math.cos(angle_radians);
  const sin_alpha = Math.sin(angle_radians);

  const matrix = new Array<number>(16);

  matrix[0] = dir.x * dir.x * (1 - cos_alpha) + cos_alpha;
  matrix[4] = dir.x * dir.y * (1 - cos_alpha) - dir.z * sin_alpha;
  matrix[8] = dir.x * dir.z * (1 - cos_alpha) + dir.y * sin_alpha;
  matrix[12] = 0;

  matrix[1] = dir.x * dir.y * (1 - cos_alpha) + dir.z * sin_alpha;
  matrix[5] = dir.y * dir.y * (1 - cos_alpha) + cos_alpha;
  matrix[9] = dir.y * dir.z * (1 - cos_alpha) - dir.x * sin_alpha;
  matrix[13] = 0;

  matrix[2] = dir.x * dir.z * (1 - cos_alpha) - dir.y * sin_alpha;
  matrix[6] = dir.y * dir.z * (1 - cos_alpha) + dir.x * sin_alpha;
  matrix[10] = dir.z * dir.z * (1 - cos_alpha) + cos_alpha;
  matrix[14] = 0;

  matrix[3] = 0;
  matrix[7] = 0;
  matrix[11] = 0;
  matrix[15] = 1;

  // SetRotationCenter
  matrix[12] = position.x - (matrix[0] * position.x + matrix[4] * position.y + matrix[8] * position.z);
  matrix[13] = position.y - (matrix[1] * position.x + matrix[5] * position.y + matrix[9] * position.z);
  matrix[14] = position.z - (matrix[2] * position.x + matrix[6] * position.y + matrix[10] * position.z);

  return new THREE.Matrix4().fromArray(matrix);
}

export function calculatePlanesIntersectionLine(
  pl1: THREE.Plane,
  pl2: THREE.Plane,
  eps = 1e-8
): { origin: THREE.Vector3; direction: THREE.Vector3 } | undefined {
  const origin = new THREE.Vector3();
  const denomZ = pl2.normal.x * pl1.normal.y - pl1.normal.x * pl2.normal.y;
  if (Math.abs(denomZ) > eps) {
    origin.set(
      (pl2.normal.y * pl1.constant - pl1.normal.y * pl2.constant) / denomZ,
      (-pl2.normal.x * pl1.constant + pl1.normal.x * pl2.constant) / denomZ,
      0
    );
  } else {
    const denomY = pl2.normal.x * pl1.normal.z - pl1.normal.x * pl2.normal.z;
    if (Math.abs(denomY) > eps) {
      origin.set(
        (pl2.normal.z * pl1.constant - pl1.normal.z * pl2.constant) / denomY,
        0,
        (-pl2.normal.x * pl1.constant + pl1.normal.x * pl2.constant) / denomY
      );
    } else {
      const denomX = pl2.normal.y * pl1.normal.z - pl1.normal.y * pl2.normal.z;
      if (Math.abs(denomX) > eps) {
        origin.set(
          0,
          (pl2.normal.z * pl1.constant - pl1.normal.z * pl2.constant) / denomX,
          (-pl2.normal.y * pl1.constant + pl1.normal.y * pl2.constant) / denomX
        );
      } else {
        // if all 3 denoms equals 0 then normal equals to +- pl2.normal
        //const scalarProduct = pl1.normal.dot(pl2.normal);
        //if (((scalarProduct > 0) && (Math.abs(pl1.constant - pl2.constant) < eps)) ||
        //    ((scalarProduct < 0) && (Math.abs(pl1.constant + pl2.constant) < eps)))
        //  return PLANES_OVERLAP;
        //else
        //  return NO_INTERSECTION;
        return undefined;
      }
    }
  }

  const direction = new THREE.Vector3().crossVectors(pl1.normal, pl2.normal).normalize();

  return {
    origin: origin,
    direction: direction
  };
}

export function cylinderGeometryFromLine(ln: THREE.Line3, r: number): THREE.BufferGeometry {
  const h = ln.distance();
  const q = new THREE.Quaternion().setFromUnitVectors(
    new THREE.Vector3(0, 1, 0),
    new THREE.Vector3().subVectors(ln.end, ln.start).normalize()
  );
  const transl1 = new THREE.Matrix4().makeTranslation(0, -h / 2, 0);
  const transl2 = new THREE.Matrix4().makeTranslation(ln.end.x, ln.end.y, ln.end.z);
  const rotation = new THREE.Matrix4().makeRotationFromQuaternion(q);
  const m = transl1.clone().premultiply(rotation).premultiply(transl2);
  const ret = new THREE.CylinderGeometry(r, r, h).applyMatrix4(m);
  return ret.deleteAttribute('uv');
}

export function sphereGeometryFromPoint(pt: THREE.Vector3, r: number): THREE.BufferGeometry {
  const ret = new THREE.SphereGeometry(r).translate(pt.x, pt.y, pt.z);
  return ret.deleteAttribute('uv');
}

export function triangleGeometryFromPoints(
  edges: [THREE.Vector3, THREE.Vector3, THREE.Vector3],
  doubleSided = false
): THREE.BufferGeometry {
  const positions = new Float32Array(doubleSided ? 18 : 9);
  positions[0] = edges[0].x;
  positions[1] = edges[0].y;
  positions[2] = edges[0].z;
  positions[3] = edges[1].x;
  positions[4] = edges[1].y;
  positions[5] = edges[1].z;
  positions[6] = edges[2].x;
  positions[7] = edges[2].y;
  positions[8] = edges[2].z;
  if (doubleSided) {
    const cb = new THREE.Vector3().subVectors(edges[2], edges[1]);
    const ab = new THREE.Vector3().subVectors(edges[0], edges[1]);
    cb.cross(ab).normalize().multiplyScalar(-0.01);
    positions[9] = edges[0].x + cb.x;
    positions[10] = edges[0].y + cb.y;
    positions[11] = edges[0].z + cb.z;
    positions[12] = edges[1].x + cb.x;
    positions[13] = edges[1].y + cb.y;
    positions[14] = edges[1].z + cb.z;
    positions[15] = edges[2].x + cb.x;
    positions[16] = edges[2].y + cb.y;
    positions[17] = edges[2].z + cb.z;
  }
  const indices = new Uint16Array(doubleSided ? 6 : 3);
  indices[0] = 0;
  indices[1] = 1;
  indices[2] = 2;
  if (doubleSided) {
    indices[3] = 3;
    indices[4] = 5;
    indices[5] = 4;
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setIndex(new THREE.BufferAttribute(indices, 1));
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.computeVertexNormals();
  return geometry;
}

export function fluoroAnglesFromCamera(toCameraDir: THREE.Vector3): { raoLao: number; caudCran: number } {
  const zeroToCameraDirection = new THREE.Vector3(0, -1, 0);
  const raoLaoPlaneNormal = new THREE.Vector3(0, 0, 1);
  const caudCranPlaneNormal = new THREE.Vector3(1, 0, 0);

  const raoLaoVector = toCameraDir.clone().cross(raoLaoPlaneNormal).negate().cross(raoLaoPlaneNormal);
  const raoLaoAbs = raoLaoVector.lengthSq() > 1e-6 ? zeroToCameraDirection.angleTo(raoLaoVector) : 0;

  const caudCranVector = toCameraDir.clone().cross(caudCranPlaneNormal).negate().cross(caudCranPlaneNormal);
  const caudCranAbs = caudCranVector.lengthSq() > 1e-6 ? zeroToCameraDirection.angleTo(caudCranVector) : 0;

  const angle1 = raoLaoVector.angleTo(caudCranPlaneNormal);
  const angle2 = caudCranVector.angleTo(raoLaoPlaneNormal);

  return {
    raoLao: ((angle1 <= Math.PI / 2 ? raoLaoAbs : -raoLaoAbs) * 180) / Math.PI,
    caudCran: ((angle2 <= Math.PI / 2 ? caudCranAbs : -caudCranAbs) * 180) / Math.PI
  };
}

export function fluoroSCurveFromPlane(
  origin: THREE.Vector3,
  normal: THREE.Vector3,
  numSegments = 180
): Array<{ raoLao: number; caudCran: number }> {
  if (normal.length() < 1e-6) {
    throw new Error('Normal should be of non-zero length');
  }
  if (Math.abs(normal.x) < 1e-6 && Math.abs(normal.z) < 1e-6) {
    throw new Error('Normal should not lie in zx-plane because of ambiguity');
  }

  const toCameraDir = new THREE.Vector3(0, 1, 0).cross(normal);
  let stepRadians = Math.PI / numSegments;
  if (Math.abs(normal.x) >= Math.abs(normal.z)) {
    if (normal.x > 0) {
      stepRadians = -stepRadians;
    } else {
      toCameraDir.negate();
    }
  } else {
    if (normal.z > 0) {
      toCameraDir.negate();
    } else {
      stepRadians = -stepRadians;
    }
  }

  const ret = new Array<{ raoLao: number; caudCran: number }>(numSegments + 1);
  ret[0] = fluoroAnglesFromCamera(toCameraDir);
  ret[numSegments] = fluoroAnglesFromCamera(toCameraDir.clone().negate());
  for (let i = 1, angleRadians = stepRadians; i < numSegments; ++i, angleRadians += stepRadians) {
    const rotation = makeRotationMatrix(origin, normal, angleRadians);
    ret[i] = fluoroAnglesFromCamera(toCameraDir.clone().transformDirection(rotation));
  }

  return ret;
}

export function makeCoordinateSystemYz(
  origin: THREE.Vector3,
  yVector: THREE.Vector3,
  zVector: THREE.Vector3
): THREE.Matrix4 {
  const _xVector = yVector.clone().cross(zVector).normalize();
  const _yVector = zVector.clone().cross(_xVector).normalize();
  const _zVector = zVector.clone().normalize();
  return new THREE.Matrix4().makeBasis(_xVector, _yVector, _zVector).setPosition(origin);
}

export function createRotatedZVector(yzAngleRad: number, zxAngleRad: number): THREE.Vector3 {
  if (
    yzAngleRad != 0 &&
    zxAngleRad != 0 &&
    Math.abs(yzAngleRad) <= Math.PI / 2 != Math.abs(zxAngleRad) <= Math.PI / 2
  ) {
    throw new Error('Logic error: Angles discrepancy');
  }

  if (Math.abs(Math.abs(yzAngleRad) - Math.PI / 2) < 1e-6 || Math.abs(Math.abs(zxAngleRad) - Math.PI / 2) < 1e-6) {
    // Ambiguity: instead of single vector we've got whole XY-plane that is a match.
    throw new Error('Logic error: Ambiguous case');
  }

  const xAxis = new THREE.Vector3(1, 0, 0);
  const yAxis = new THREE.Vector3(0, 1, 0);
  const zAxis = new THREE.Vector3(0, 0, 1);

  // get yz rotated z axis
  const yzRotation = new THREE.Matrix4().makeRotationX(yzAngleRad);
  const yzOptimizedZ = zAxis.clone().transformDirection(yzRotation);

  // get zx rotated z axis
  const zxRotation = new THREE.Matrix4().makeRotationY(zxAngleRad);
  const zxOptimizedZ = zAxis.clone().transformDirection(zxRotation);

  // get z axis that contains both yz and zx rotations
  // yz and zx angles are angles between original z and their z projections on yz and zx planes correspondingly,
  // so z with rotations is an intersection of the planes (see below)
  const pl1Normal = yzOptimizedZ.clone().negate().cross(xAxis.clone().sub(yzOptimizedZ));
  const pl2Normal = zxOptimizedZ.clone().negate().cross(yAxis.clone().sub(zxOptimizedZ));
  const rotatedZ = pl1Normal.cross(pl2Normal).normalize();
  if (Math.abs(yzAngleRad) > Math.PI / 2 && Math.abs(zxAngleRad) > Math.PI / 2) {
    rotatedZ.negate();
  }
  return rotatedZ;
}

export function createRotatedCoordinateSystem(
  yzAngleRad: number,
  zxAngleRad: number,
  xyAngleRad: number
): THREE.Matrix4 {
  if (Math.abs(yzAngleRad) + 1e-6 >= Math.PI / 2 || Math.abs(zxAngleRad) + 1e-6 >= Math.PI / 2) {
    // Ambiguity: optimized y axis calculation is not ready for coordinate axis swap
    throw new Error('Logic error: Ambiguous case');
  }

  const optimizedZ = createRotatedZVector(yzAngleRad, zxAngleRad);

  // get y axis that lies on yz plane and is perpendicular to rotated z
  const optimizedY = optimizedZ
    .clone()
    .cross(new THREE.Vector3(1, 0, 0))
    .normalize();

  return makeCoordinateSystemYz(new THREE.Vector3(0, 0, 0), optimizedY, optimizedZ).multiply(
    new THREE.Matrix4().makeRotationZ(xyAngleRad)
  );
}

export function cameraDirectionFromFluoroAngles(
  raoLaoDeg: number,
  caudCranDeg: number,
  perpendicular?: THREE.Vector3
): THREE.Vector3 {
  if (raoLaoDeg != 0 && caudCranDeg != 0 && Math.abs(raoLaoDeg) <= 90 != Math.abs(caudCranDeg) <= 90) {
    throw new Error('Logic error: Angles discrepancy');
  }
  if (Math.abs(Math.abs(raoLaoDeg) - 90) < 1e-4 || Math.abs(Math.abs(caudCranDeg) - 90) < 1e-4) {
    // Corner case: The whole world's XY-plane is a match, not the single vector - ambiguity.
    // Thus we are solving ambiguity by provisioning of the vector that is perpendicular to the desired camera direction.
    if (!perpendicular) {
      throw new Error('Logic error: Ambiguous case. Perpendicular is required');
    }
    return new THREE.Vector3(0, raoLaoDeg < 0 ? -1 : 1, 0).cross(perpendicular).normalize();
  } else {
    const rotatedZ = createRotatedZVector((caudCranDeg * Math.PI) / 180, (raoLaoDeg * Math.PI) / 180);
    // ToCamera direction vector must be Y-axis in table CS
    // Thus we need swap Z and Y coordinates of the found vector
    return new THREE.Vector3(rotatedZ.x, -rotatedZ.z, -rotatedZ.y);
  }
}

export function areLinesParallel(line1: THREE.Line3, line2: THREE.Line3, tolerance = 0.00001): boolean {
  return (
    new THREE.Vector3().crossVectors(line1.delta(new THREE.Vector3()), line2.delta(new THREE.Vector3())).length() <
    tolerance
  );
}

export function getPlaneLineIntersectionPoint(
  plO: THREE.Vector3,
  plN: THREE.Vector3,
  lnPt: THREE.Vector3,
  lnDir: THREE.Vector3,
  tolerance = 0.00001
): THREE.Vector3 | undefined {
  const denom = plN.dot(lnDir);
  if (Math.abs(denom) < tolerance) {
    return undefined;
  }
  const t = -(lnPt.dot(plN) - plO.dot(plN)) / denom;
  return lnPt.clone().addScaledVector(lnDir, t);
}

export function getCommonPerpendicularLine(
  lnPt1: THREE.Vector3,
  lnDir1: THREE.Vector3,
  lnPt2: THREE.Vector3,
  lnDir2: THREE.Vector3,
  tolerance = 0.00001
): THREE.Line3 | undefined {
  const dir = new THREE.Vector3().crossVectors(lnDir1, lnDir2);
  if (dir.length() < tolerance) {
    return undefined;
  }
  const n1 = new THREE.Vector3().crossVectors(dir, lnDir1);
  const n2 = new THREE.Vector3().crossVectors(dir, lnDir2);
  const p1 = getPlaneLineIntersectionPoint(lnPt2, n2, lnPt1, lnDir1);
  const p2 = getPlaneLineIntersectionPoint(lnPt1, n1, lnPt2, lnDir2);
  if (p1 && p2) {
    return new THREE.Line3(p1, p2);
  }
  return undefined;
}

export function findNearestSegmentIndex(polyline: Array<THREE.Vector3>, point: THREE.Vector3): number | undefined {
  const dir = new THREE.Vector3();
  const plane = new THREE.Plane();
  let minDist = -1;
  let j = -1;
  for (let i = 0, cnt = polyline.length - 1; i < cnt; ++i) {
    const origin = polyline[i];
    dir
      .copy(polyline[i + 1])
      .sub(origin)
      .normalize();
    plane.setFromNormalAndCoplanarPoint(dir, origin);
    const len = dir.length();
    const dist = plane.distanceToPoint(point);
    if (dist >= 0 && dist < len && (minDist < 0 || dist < minDist)) {
      minDist = dist;
      j = i;
    }
  }

  if (j < 0) {
    return undefined;
  }

  return j;
}

export function distancePointToPlane(point: THREE.Vector3, plNormal: THREE.Vector3, plOrigin: THREE.Vector3): number {
  const plConstant = -plOrigin.dot(plNormal);
  return plNormal.dot(point) + plConstant;
}

export function findNearestPolylinePointToPoint(polyline: Array<THREE.Vector3>, point: THREE.Vector3): number {
  let idx = -1;
  let minDist = -1;
  for (let i = 0; i < polyline.length; ++i) {
    const dist = polyline[i].distanceToSquared(point);
    if (idx < 0 || dist < minDist) {
      idx = i;
      minDist = dist;
    }
  }
  return idx;
}

export function findNearestPolylinePointToPlane(
  polyline: Array<THREE.Vector3>,
  plNormal: THREE.Vector3,
  plOrigin: THREE.Vector3
): number {
  const plConstant = -plOrigin.dot(plNormal);
  let idx = -1;
  let minDist = -1;
  for (let i = 0; i < polyline.length; ++i) {
    const dist = Math.abs(plNormal.dot(polyline[i]) + plConstant);
    if (idx < 0 || dist < minDist) {
      idx = i;
      minDist = dist;
    }
  }
  return idx;
}

export function findNearestCenterlinePointToPoint(
  clPoints: Array<THREE.Vector3>,
  clNormals: Array<THREE.Vector3>,
  point: THREE.Vector3
): number {
  let idx = -1;
  let minDist = -1;
  for (let i = 0; i < clPoints.length; ++i) {
    const normal = clNormals[i];
    const dist = Math.abs(normal.dot(point) - clPoints[i].dot(normal));
    if (idx < 0 || dist < minDist) {
      idx = i;
      minDist = dist;
    }
  }
  return idx;
}

export function bufferGeometryProps(bufferGeometry: THREE.BufferGeometry): { area: number; volume: number } {
  if (bufferGeometry.index == null) return { area: 0, volume: 0 };
  const vertices = bufferGeometry.attributes.position.array;
  const indices = bufferGeometry.index.array;
  let area = 0;
  let volume = 0;
  let dx0 = 0,
    dx1 = 0,
    dy0 = 0,
    dy1 = 0,
    dz0 = 0,
    dz1 = 0,
    pdx = 0,
    pdy = 0,
    pdz = 0,
    z = 0;
  for (let i = 0; i < indices.length; i += 3) {
    const i0 = indices[i] * 3;
    const i1 = indices[i + 1] * 3;
    const i2 = indices[i + 2] * 3;
    const pt0 = [vertices[i0], vertices[i0 + 1], vertices[i0 + 2]];
    const pt1 = [vertices[i1], vertices[i1 + 1], vertices[i1 + 2]];
    const pt2 = [vertices[i2], vertices[i2 + 1], vertices[i2 + 2]];
    dx0 = pt1[0] - pt0[0];
    dy0 = pt1[1] - pt0[1];
    dz0 = pt1[2] - pt0[2];
    dx1 = pt2[0] - pt0[0];
    dy1 = pt2[1] - pt0[1];
    dz1 = pt2[2] - pt0[2];
    pdx = dy0 * dz1 - dz0 * dy1;
    pdy = dz0 * dx1 - dx0 * dz1;
    pdz = dx0 * dy1 - dy0 * dx1;
    area += Math.sqrt(pdx * pdx + pdy * pdy + pdz * pdz) / 2.0;
    z = (pt0[2] + pt1[2] + pt2[2]) / 3.0;
    volume += (z * pdz) / 2.0; // consider [(z-minZ)*pdz/2.0] for error accumulation reduction
  }
  return { area: area, volume: volume };
}

export class CustomCurve extends THREE.Curve<THREE.Vector3> {
  private _points: Array<THREE.Vector3>;

  public constructor(points: Array<THREE.Vector3>) {
    super();
    this._points = points;
  }

  public getPoint(t: number): THREE.Vector3 {
    const index = (this._points.length - 1) * t;
    const floorIndex = Math.floor(index);
    const ceilIndex = Math.ceil(index);

    const startPoint = this._points[floorIndex];
    const endPoint = this._points[ceilIndex];

    return startPoint.clone().lerp(endPoint, index - floorIndex);
  }
}

export function __private_findThirdSideLengthOfGenericTriangle(
  firstSideLength: number,
  secondSideLength: number,
  thirdToFirstSideAngle: number
): number | undefined {
  if (firstSideLength < 0 || secondSideLength < 0) throw new Error('Logic Error');
  if (thirdToFirstSideAngle < 0 || thirdToFirstSideAngle > Math.PI) throw new Error('Logic Error');
  const angle = thirdToFirstSideAngle <= Math.PI / 2 ? thirdToFirstSideAngle : Math.PI - thirdToFirstSideAngle;
  const tan = Math.tan(angle);
  const tanSq = tan * tan;
  const a = tanSq + 1;
  const b = (thirdToFirstSideAngle < Math.PI / 2 ? -2 : 2) * firstSideLength;
  const c = firstSideLength * firstSideLength - secondSideLength * secondSideLength;
  const d = b * b - 4 * a * c; // d - determinant for a*x^2 + b*x + c = 0
  if (d < 0) return undefined; // discrepancy between sides and angle. Not throw but undefined here - there is dependency on this logic in client code!
  const dSqrt = Math.sqrt(d);
  const x1 = (-b + dSqrt) / (2 * a);
  const x2 = (-b - dSqrt) / (2 * a);
  const x = thirdToFirstSideAngle > 0 && thirdToFirstSideAngle < Math.PI / 4 ? x2 : x1;
  return x / Math.cos(angle);
}

export function makeRaysJointLine(
  rayZPt: THREE.Vector3,
  rayZDir: THREE.Vector3,
  rayXPt: THREE.Vector3,
  rayXDir: THREE.Vector3,
  jointLineLen: number,
  zRayDisplacement: number,
  tolerance = 0.00001
): THREE.Line3 | undefined {
  if (tolerance < 0 || tolerance > 0.1) throw new Error('Invalid input parameter');
  if (jointLineLen < tolerance) throw new Error('Invalid input parameter');
  if (rayZDir.length() < tolerance) throw new Error('Invalid input parameter');
  if (rayXDir.length() < tolerance) throw new Error('Invalid input parameter');
  const commonPerpendicular = getCommonPerpendicularLine(rayZPt, rayZDir, rayXPt, rayXDir, tolerance);
  if (!commonPerpendicular) {
    // Same Ray
    return undefined;
  }
  const commonPerpendicularLen = commonPerpendicular.distance();
  if (Math.abs(commonPerpendicularLen - jointLineLen) <= tolerance) {
    // Distance between rays (common perpendicular length) equals to desired joint length
    return commonPerpendicular;
  }
  if (commonPerpendicularLen - jointLineLen > tolerance) {
    // Distance between rays (common perpendicular length) is bigger than desired joint length
    return undefined;
  }
  const vecX = rayXDir.clone().normalize();
  const vecZ = rayZDir.clone().normalize();
  let xRayDisplacement: number | undefined = undefined;
  if (commonPerpendicularLen < tolerance) {
    // Rays are crossing
    if (zRayDisplacement < tolerance) {
      // Output line follows RayX
      xRayDisplacement = jointLineLen;
    } else {
      // Plane made by two rays
      const angleBetweenRays = vecX.angleTo(vecZ);
      if (Math.abs(angleBetweenRays - Math.PI / 2) < tolerance) {
        // Rays are perpendicular
        xRayDisplacement = Math.sqrt(jointLineLen * jointLineLen - zRayDisplacement * zRayDisplacement);
      } else {
        // Generic case
        xRayDisplacement = __private_findThirdSideLengthOfGenericTriangle(
          zRayDisplacement,
          jointLineLen,
          angleBetweenRays
        );
      }
    }
  } else {
    // There is distance between rays
    if (zRayDisplacement < tolerance) {
      // Plane made by xRay an common perpendicular
      xRayDisplacement = Math.sqrt(jointLineLen * jointLineLen - commonPerpendicularLen * commonPerpendicularLen);
    } else {
      // Generic case
      const angleBetweenRays = vecX.angleTo(vecZ);
      const jointLineLenProj = Math.sqrt(jointLineLen * jointLineLen - commonPerpendicularLen * commonPerpendicularLen);
      xRayDisplacement = __private_findThirdSideLengthOfGenericTriangle(
        zRayDisplacement,
        jointLineLenProj,
        angleBetweenRays
      );
    }
  }
  if (xRayDisplacement === undefined) {
    // negative determinant case = distance from desired point with 'zRayDisplacement' on zRay is more than desired 'jointLineLen'
    return undefined;
  }
  return new THREE.Line3(
    commonPerpendicular.start.clone().addScaledVector(vecZ, zRayDisplacement),
    commonPerpendicular.end.clone().addScaledVector(vecX, xRayDisplacement)
  );
}

export function findCoordinateSystemVector(z: THREE.Vector3): THREE.Vector3 {
  const a = z.x;
  const b = z.y;
  const c = z.z;

  const vec = new THREE.Vector3();

  // Find the largest coefficient (to use as devisor)
  if (Math.abs(a) > Math.abs(b) && Math.abs(a) > Math.abs(c)) {
    vec.set(-b / a, 1, 0); // A
  } else if (Math.abs(b) > Math.abs(c)) {
    vec.set(0, -c / b, 1); // B
  } else {
    vec.set(1, 0, -a / c); // C
  }

  return vec.normalize();
}

export function makePlaneCoordinateSystem(normal: THREE.Vector3, origin: THREE.Vector3): THREE.Matrix4 {
  const a = normal.x;
  const b = normal.y;
  const c = normal.z;

  const vecX = new THREE.Vector3();
  const vecY = new THREE.Vector3();
  const vecZ = new THREE.Vector3(a, b, c);

  // Find the largest coefficient (to use as devisor)
  if (Math.abs(a) > Math.abs(b) && Math.abs(a) > Math.abs(c)) {
    // A
    vecX.set(-b / a, 1, 0);
    vecY.set(-c / a, 0, 1);
  } else if (Math.abs(b) > Math.abs(c)) {
    // B
    vecX.set(0, -c / b, 1);
    vecY.set(1, -a / b, 0);
  } else {
    // C
    vecX.set(1, 0, -a / c);
    vecY.set(0, 1, -b / c);
  }

  vecX.normalize();
  vecY.normalize();
  vecZ.normalize();

  return new THREE.Matrix4().makeBasis(vecX, vecY, vecZ).setPosition(origin);
}

export function makePlaneCoordinateSystem2(plane: THREE.Plane): THREE.Matrix4 {
  return makePlaneCoordinateSystem(plane.normal, plane.normal.clone().multiplyScalar(-plane.constant));
}

export enum CatmullRomCurve3Type {
  Centripetal = 'centripetal',
  Chordal = 'chordal',
  CatmullRom = 'catmullrom'
}
/**
 * @param points An array of points
 * @param closed Whether the curve is closed
 * @param multiplier Multiplier for original point segments division into pieces. Expects an `Integer`. Default is `5`
 * @param curveType Type of the curve. Expects a `CatmullRomCurve3Type`. Default is `Centripetal`
 * @param tension Tension of the curve. Expects a `Float`. is Default `0.5`
 */
export function getCatmullRomCurve3Points(
  points: Array<THREE.Vector3>,
  closed: boolean,
  multiplier = 5,
  curveType = CatmullRomCurve3Type.Centripetal,
  tension?: number
): Array<THREE.Vector3> {
  const curve = new THREE.CatmullRomCurve3(points, closed, curveType, tension);
  const curvePoints = curve.getPoints((points.length - (closed ? 0 : 1)) * multiplier);
  if (closed) {
    // in case of closed curve we need to pop out
    // the last point as it is equal to first point
    curvePoints.pop();
  }
  return curvePoints;
}

function _lengthSq(v: [number, number, number]): number {
  return v[0] * v[0] + v[1] * v[1] + v[2] * v[2];
}

function _computeSortedEdgeInfos(
  v0: [number, number, number],
  v1: [number, number, number],
  v2: [number, number, number]
): Array<{ lengthSq: number; index: number }> {
  // [chebotarev] ported from MatSDK.Math2.Core\src\triangle_operations.cpp::compute_sorted_edge_infos
  const a = { lengthSq: _lengthSq([v0[0] - v1[0], v0[1] - v1[1], v0[2] - v1[2]]), index: 0 };
  const b = { lengthSq: _lengthSq([v1[0] - v2[0], v1[1] - v2[1], v1[2] - v2[2]]), index: 1 };
  const c = { lengthSq: _lengthSq([v2[0] - v0[0], v2[1] - v0[1], v2[2] - v0[2]]), index: 2 };
  const result = new Array<{ lengthSq: number; index: number }>(3);
  // optimized sorting of 3 elements
  result[(a.lengthSq > b.lengthSq ? 1 : 0) + (a.lengthSq > c.lengthSq ? 1 : 0)] = a;
  result[(b.lengthSq >= a.lengthSq ? 1 : 0) + (b.lengthSq > c.lengthSq ? 1 : 0)] = b;
  result[(c.lengthSq >= a.lengthSq ? 1 : 0) + (c.lengthSq >= b.lengthSq ? 1 : 0)] = c;
  return result;
}

function _getIndexOfLongestEdge(
  v0: [number, number, number],
  v1: [number, number, number],
  v2: [number, number, number]
): number {
  // [chebotarev] ported from MatSDK.Math2.Core\src\triangle_operations.cpp::get_index_of_longest_edge
  return _computeSortedEdgeInfos(v0, v1, v2)[2].index;
}

function _detTriPlane(x0: number, y0: number, x1: number, y1: number, x2: number, y2: number): number {
  // [chebotarev] ported from MatSDK.Math2.Core\src\triangle_operations.cpp::det_tri_plane
  return (x0 - x2) * (y1 - y2) - (y0 - y2) * (x1 - x2);
}

function _getTriangleNormalPrecise(v: ArrayLike<number>, i: ArrayLike<number>, idx: number): [number, number, number] {
  // [chebotarev] ported from MatSDK.Math2.Core\src\triangle_operations.cpp::get_normal
  const i0 = i[idx] * 3;
  const i1 = i[idx + 1] * 3;
  const i2 = i[idx + 2] * 3;

  const points: Array<[number, number, number]> = [
    [v[i0], v[i0 + 1], v[i0 + 2]],
    [v[i1], v[i1 + 1], v[i1 + 2]],
    [v[i2], v[i2 + 1], v[i2 + 2]]
  ];

  const j = _getIndexOfLongestEdge(points[0], points[1], points[2]);

  const normal: [number, number, number] = [0, 0, 0];
  for (let i = 0; i < 3; ++i) {
    const a = (i + 1) % 3;
    const b = (i + 2) % 3;
    normal[i] = _detTriPlane(
      points[j][a],
      points[j][b],
      points[(j + 1) % 3][a],
      points[(j + 1) % 3][b],
      points[(j + 2) % 3][a],
      points[(j + 2) % 3][b]
    );
  }

  return normal;
}

function _getTriangleNormalFast(v: ArrayLike<number>, i: ArrayLike<number>, idx: number): [number, number, number] {
  const i0 = i[idx] * 3;
  const i1 = i[idx + 1] * 3;
  const i2 = i[idx + 2] * 3;

  return [
    _detTriPlane(v[i0 + 1], v[i0 + 2], v[i1 + 1], v[i1 + 2], v[i2 + 1], v[i2 + 2]),
    _detTriPlane(v[i0 + 2], v[i0], v[i1 + 2], v[i1], v[i2 + 2], v[i2]),
    _detTriPlane(v[i0], v[i0 + 1], v[i1], v[i1 + 1], v[i2], v[i2 + 1])
  ];
}

export function getTriangleNormalPrecise(v: ArrayLike<number>, i: ArrayLike<number>, idx: number): THREE.Vector3 {
  // [chebotarev] ported from MatSDK.Math2.Core\src\triangle_operations.cpp::get_normal
  const normal = _getTriangleNormalPrecise(v, i, idx);
  return new THREE.Vector3().fromArray(normal).normalize();
}

export function getTriangleAreaPrecise(v: ArrayLike<number>, i: ArrayLike<number>, idx: number): number {
  // [chebotarev] ported from MatSDK.Math2.Core\src\triangle_operations.cpp::get_area
  const normal = _getTriangleNormalPrecise(v, i, idx);
  const length = Math.sqrt(_lengthSq(normal));
  return length / 2;
}

export function getTriangleNormalFast(v: ArrayLike<number>, i: ArrayLike<number>, idx: number): THREE.Vector3 {
  const normal = _getTriangleNormalFast(v, i, idx);
  return new THREE.Vector3().fromArray(normal).normalize();
}

export function getTriangleAreaFast(v: ArrayLike<number>, i: ArrayLike<number>, idx: number): number {
  const normal = _getTriangleNormalFast(v, i, idx);
  const length = Math.sqrt(_lengthSq(normal));
  return length / 2;
}

export function bufferGeometryWeightedGravityCenter(
  bufferGeometry: THREE.BufferGeometry,
  precise = false
): { vector: THREE.Vector3; weight: number } {
  // [chebotarev] ported from  MatSDK.TriangleSurface.Core\src\basic_geometry_queries.cpp::barycenter
  const ret = { vector: new THREE.Vector3(), weight: 0 };
  if (bufferGeometry.index !== null) {
    const vertices = bufferGeometry.attributes.position.array;
    const indices = bufferGeometry.index.array;

    const _weightGetter = precise ? getTriangleAreaPrecise : getTriangleAreaFast;

    for (let idx = 0; idx < indices.length; idx += 3) {
      const i = indices[idx] * 3;
      const j = indices[idx + 1] * 3;
      const k = indices[idx + 2] * 3;

      const trWeight = _weightGetter(vertices, indices, idx);

      ret.vector.x += (vertices[i] + vertices[j] + vertices[k]) * trWeight;
      ret.vector.y += (vertices[i + 1] + vertices[j + 1] + vertices[k + 1]) * trWeight;
      ret.vector.z += (vertices[i + 2] + vertices[j + 2] + vertices[k + 2]) * trWeight;

      ret.weight += 3 * trWeight;
    }
  }
  return ret;
}

export function bufferGeometryGravityCenter(bufferGeometry: THREE.BufferGeometry, precise = false): THREE.Vector3 {
  const gravityVector = bufferGeometryWeightedGravityCenter(bufferGeometry, precise);
  return gravityVector.vector.divideScalar(gravityVector.weight);
}

export function splineWeightedGravityCenter(
  splinePts: Array<THREE.Vector3>,
  isClosed: boolean
): { vector: THREE.Vector3; weight: number } {
  const ret = { vector: new THREE.Vector3(), weight: 0 };
  if (splinePts.length > 1) {
    const pts = isClosed ? [...splinePts, splinePts[0]] : splinePts;
    for (let i = 0; i < pts.length - 1; ++i) {
      const p0 = pts[i];
      const p1 = pts[i + 1];

      const lnWeight = p0.distanceTo(p1);

      ret.vector.x += (p0.x + p1.x) * lnWeight;
      ret.vector.y += (p0.y + p1.y) * lnWeight;
      ret.vector.z += (p0.z + p1.z) * lnWeight;

      ret.weight += 2 * lnWeight;
    }
  }
  return ret;
}

export function barycenter(bufferGeometry: THREE.BufferGeometry): THREE.Vector3 {
  // [chebotarev] ported from  MatSDK.TriangleSurface.Core\src\basic_geometry_queries.cpp::barycenter
  const gravityPoint = bufferGeometryWeightedGravityCenter(bufferGeometry);
  if (gravityPoint.weight > 0) {
    return gravityPoint.vector.divideScalar(gravityPoint.weight);
  } else {
    return gravityPoint.vector;
  }
}

export function checkIsPlaneInsideBox(
  points: Array<THREE.Vector3>,
  plane: THREE.Plane,
  sliceDistance: number
): boolean {
  let hasPositiveDistance = false;
  let hasNegativeDistance = false;

  for (const point of points) {
    const distance = plane.distanceToPoint(point);

    if (distance >= -sliceDistance) {
      hasPositiveDistance = true;
    }
    if (distance <= sliceDistance) {
      hasNegativeDistance = true;
    }

    if (hasPositiveDistance && hasNegativeDistance) {
      return true;
    }
  }

  return false;
}

export function getOpacityByDistance(distanceToPoint: number, isAnywayVisible: boolean, maxDistance: number): number {
  const relDist = Math.min(Math.max(distanceToPoint / maxDistance, 0.25), isAnywayVisible ? 0.75 : 1);
  const opacity = 1 - relDist;

  return opacity;
}

export function getClosestSplinePointToRay(
  splinePoints: Array<THREE.Vector3>,
  ray: THREE.Ray
): THREE.Vector3 | undefined {
  let prevPt = splinePoints[splinePoints.length - 1];
  let minDistSq = Number.MAX_SAFE_INTEGER;
  let minPt: THREE.Vector3 | undefined = undefined;
  for (const pt of splinePoints) {
    const ptOnSegment = new THREE.Vector3();
    const distSq = ray.distanceSqToSegment(prevPt, pt, undefined, ptOnSegment);
    if (distSq < minDistSq) {
      minDistSq = distSq;
      minPt = ptOnSegment;
    }
    prevPt = pt;
  }
  return minPt;
}
